import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import questionsConfig from '../config/questions.json';

// Admin phone numbers
const ADMIN_PHONE_NUMBERS = [
  '+15407939110',
];

function Survey() {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const questions = questionsConfig.questions;
  const currentQuestion = questions[currentQuestionIndex];
  
  // Calculate progress based on visible questions only
  const isQuestionVisible = (q) => {
    if (!q.conditional) return true;
    const { dependsOn, showWhen } = q.conditional;
    const dependentAnswer = answers[dependsOn];
    return showWhen.includes(dependentAnswer);
  };
  
  const visibleQuestions = questions.filter(q => isQuestionVisible(q));
  const currentVisibleIndex = visibleQuestions.findIndex(q => q.id === currentQuestion.id);
  const progress = currentVisibleIndex >= 0 ? ((currentVisibleIndex + 1) / visibleQuestions.length) * 100 : 0;

  useEffect(() => {
    if (!currentUser) {
      navigate('/');
      return;
    }

    // Check if user is an admin - admins can always view the survey
    if (ADMIN_PHONE_NUMBERS.includes(currentUser.phoneNumber)) {
      // Admin viewing survey - don't redirect
      return;
    }

    // Check if user has already submitted a response
    const checkExistingResponse = async () => {
      try {
        const { query, where, getDocs, collection } = await import('firebase/firestore');
        const responsesRef = collection(db, 'responses');
        const q = query(responsesRef, where('userId', '==', currentUser.uid));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          // User already has a response, redirect to confirmation
          navigate('/confirmation');
        }
      } catch (error) {
        // Fallback: check localStorage
        const localResponses = JSON.parse(localStorage.getItem('localResponses') || '[]');
        const existingResponse = localResponses.find(response => response.userId === currentUser.uid);
        
        if (existingResponse) {
          navigate('/confirmation');
        }
      }
    };

    checkExistingResponse();
  }, [currentUser, navigate]);

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleSelectAll = (questionId, options) => {
    const allValues = options.map(option => option.value);
    setAnswers(prev => ({
      ...prev,
      [questionId]: allValues
    }));
  };

  const handleDeselectAll = (questionId) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: []
    }));
  };

  const handleCheckboxGroupChange = (questionId, checkboxId, isChecked, textValue = '') => {
    setAnswers(prev => {
      const currentAnswers = prev[questionId] || {};
      if (isChecked) {
        return {
          ...prev,
          [questionId]: {
            ...currentAnswers,
            [checkboxId]: textValue || true
          }
        };
      } else {
        const newAnswers = { ...currentAnswers };
        delete newAnswers[checkboxId];
        return {
          ...prev,
          [questionId]: newAnswers
        };
      }
    });
  };

  const isCurrentQuestionValid = () => {
    // If question is not visible, it doesn't need to be validated
    if (!isQuestionVisible(currentQuestion)) return true;
    
    if (!currentQuestion.required) return true;
    
    const answer = answers[currentQuestion.id];
    if (Array.isArray(answer)) {
      return answer.length > 0;
    }
    return answer !== undefined && answer !== '';
  };

  const handleNext = () => {
    // If current question is not visible, skip to next visible question
    if (!isQuestionVisible(currentQuestion)) {
      let nextIndex = currentQuestionIndex + 1;
      
      // Find next visible question
      while (nextIndex < questions.length && !isQuestionVisible(questions[nextIndex])) {
        nextIndex++;
      }
      
      if (nextIndex < questions.length) {
        setCurrentQuestionIndex(nextIndex);
      } else {
        handleSubmit();
      }
      return;
    }
    
    if (!isCurrentQuestionValid()) {
      setError('Please answer this question before continuing');
      return;
    }

    setError('');
    
    // Check if user said "no" to MLK weekend availability - save immediately and show unavailable page
    if (currentQuestion.id === 'mlkWeekendAvailability' && answers[currentQuestion.id] === 'no') {
      // Save the "no" response and navigate to unavailable confirmation
      handleSubmitUnavailable();
      return;
    }
    
    // Find next visible question
    let nextIndex = currentQuestionIndex + 1;
    while (nextIndex < questions.length && !isQuestionVisible(questions[nextIndex])) {
      nextIndex++;
    }
    
    if (nextIndex < questions.length) {
      setCurrentQuestionIndex(nextIndex);
    } else {
      handleSubmit();
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      let prevIndex = currentQuestionIndex - 1;
      
      // Find previous visible question
      while (prevIndex >= 0 && !isQuestionVisible(questions[prevIndex])) {
        prevIndex--;
      }
      
      if (prevIndex >= 0) {
        setCurrentQuestionIndex(prevIndex);
      }
    }
  };

  const handleSubmitUnavailable = async () => {
    // Check if user is an admin and warn before submitting
    const isAdmin = currentUser && ADMIN_PHONE_NUMBERS.includes(currentUser.phoneNumber);
    
    if (isAdmin) {
      const confirmed = window.confirm(
        '⚠️ You are viewing this survey as an administrator.\n\n' +
        'Submitting will create a survey response. Are you sure you want to continue?'
      );
      if (!confirmed) {
        return;
      }
    }

    setLoading(true);
    setError('');

    try {
      const userName = localStorage.getItem('userName') || 'Unknown';
      
      // Filter out epicPass question from answers (not saved to DB)
      const filteredAnswers = { ...answers };
      delete filteredAnswers.epicPass;
      
      const responseData = {
        userId: currentUser?.uid || 'unknown',
        userName: userName,
        phoneNumber: currentUser?.phoneNumber || 'Unknown',
        answers: filteredAnswers,
        submittedAt: new Date().toISOString(),
        tripDates: questionsConfig.tripInfo.dates,
        isUnavailable: true,
        isWaitlist: false,
        status: 'unavailable'
      };

      try {
        // Try to save to Firestore first
        await addDoc(collection(db, 'responses'), responseData);
        console.log('✅ Saved to Firestore successfully:', responseData);
      } catch (firestoreError) {
        console.warn('❌ Firestore error, saving locally:', firestoreError);
        
        // Fallback: save to localStorage
        const existingResponses = JSON.parse(localStorage.getItem('localResponses') || '[]');
        existingResponses.push(responseData);
        localStorage.setItem('localResponses', JSON.stringify(existingResponses));
        
        // Also save to a simple format for admin dashboard
        const adminData = {
          id: 'local-' + Date.now(),
          ...responseData,
          submittedAt: new Date(responseData.submittedAt)
        };
        
        const existingAdminData = JSON.parse(localStorage.getItem('adminData') || '[]');
        existingAdminData.push(adminData);
        localStorage.setItem('adminData', JSON.stringify(existingAdminData));
      }
      
      navigate('/confirmation?reason=unavailable');
    } catch (error) {
      setError('Failed to submit your response. Please try again.');
      console.error('Submission error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    // Check if user is an admin and warn before submitting
    const isAdmin = currentUser && ADMIN_PHONE_NUMBERS.includes(currentUser.phoneNumber);
    
    if (isAdmin) {
      const confirmed = window.confirm(
        '⚠️ You are viewing this survey as an administrator.\n\n' +
        'Submitting will create a survey response. Are you sure you want to continue?'
      );
      if (!confirmed) {
        return;
      }
    }

    setLoading(true);
    setError('');

    try {
      const userName = localStorage.getItem('userName') || 'Unknown';
      
      // Filter out epicPass question from answers (not saved to DB)
      const filteredAnswers = { ...answers };
      delete filteredAnswers.epicPass;
      
      const responseData = {
        userId: currentUser?.uid || 'unknown',
        userName: userName,
        phoneNumber: currentUser?.phoneNumber || 'Unknown',
        answers: filteredAnswers,
        submittedAt: new Date().toISOString(),
        tripDates: questionsConfig.tripInfo.dates,
        isWaitlist: !isAdmin, // Non-admins go to waitlist
        status: isAdmin ? 'confirmed' : 'waitlist'
      };

      try {
        // Try to save to Firestore first
        await addDoc(collection(db, 'responses'), responseData);
        console.log('✅ Saved to Firestore successfully:', responseData);
      } catch (firestoreError) {
        console.warn('❌ Firestore error, saving locally:', firestoreError);
        
        // Fallback: save to localStorage
        const existingResponses = JSON.parse(localStorage.getItem('localResponses') || '[]');
        existingResponses.push(responseData);
        localStorage.setItem('localResponses', JSON.stringify(existingResponses));
        
        // Also save to a simple format for admin dashboard
        const adminData = {
          id: 'local-' + Date.now(),
          ...responseData,
          submittedAt: new Date(responseData.submittedAt)
        };
        
        const existingAdminData = JSON.parse(localStorage.getItem('adminData') || '[]');
        existingAdminData.push(adminData);
        localStorage.setItem('adminData', JSON.stringify(existingAdminData));
      }
      
      navigate('/confirmation');
    } catch (error) {
      setError('Failed to submit your response. Please try again.');
      console.error('Submission error:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderQuestion = () => {
    if (!isQuestionVisible(currentQuestion)) {
      return null;
    }

    switch (currentQuestion.type) {
      case 'singleSelect':
        return (
          <div>
            {currentQuestion.id === 'transportation' ? (
              // Special handling for transportation to show seat input under the drive option
              <div>
                {currentQuestion.options.map((option) => (
                  <div key={option.value} style={{ marginBottom: '15px' }}>
                    <label
                      className={`option-item ${answers[currentQuestion.id] === option.value ? 'selected' : ''}`}
                      style={{ 
                        display: 'block',
                        marginBottom: option.value === 'drive' && answers[currentQuestion.id] === 'drive' ? '10px' : '0'
                      }}
                    >
                      <input
                        type="radio"
                        name={currentQuestion.id}
                        value={option.value}
                        checked={answers[currentQuestion.id] === option.value}
                        onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                        className="option-checkbox"
                      />
                      {option.label}
                    </label>
                    
                    {/* Show seat input if this is the drive option and it's selected */}
                    {option.value === 'drive' && answers[currentQuestion.id] === 'drive' && (
                      <div style={{ 
                        padding: '15px',
                        background: '#f0f9ff',
                        borderRadius: '12px',
                        border: '1px solid #bae6fd',
                        animation: 'fadeIn 0.3s ease'
                      }}>
                        <label style={{ 
                          display: 'block',
                          marginBottom: '8px',
                          color: '#0ea5e9',
                          fontWeight: '600',
                          fontSize: '0.95rem'
                        }}>
                          How many additional seats do you have available? (excluding yourself)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="10"
                          value={answers['transportation_seats'] || ''}
                          onChange={(e) => handleAnswerChange('transportation_seats', e.target.value === '' ? '' : parseInt(e.target.value))}
                          placeholder="0"
                          style={{
                            width: '100px',
                            padding: '8px 12px',
                            border: '2px solid #bae6fd',
                            borderRadius: '8px',
                            fontSize: '1rem',
                            outline: 'none'
                          }}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              // Regular singleSelect rendering for other questions
              <div className="option-group">
                {currentQuestion.options.map((option) => (
                  <label
                    key={option.value}
                    className={`option-item ${answers[currentQuestion.id] === option.value ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name={currentQuestion.id}
                      value={option.value}
                      checked={answers[currentQuestion.id] === option.value}
                      onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                      className="option-checkbox"
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            )}
          </div>
        );

      case 'epicPass':
        return (
          <div>
            <div className="option-group">
              {currentQuestion.options.map((option) => (
                <label
                  key={option.value}
                  className={`option-item ${answers[currentQuestion.id] === option.value ? 'selected' : ''}`}
                >
                  <input
                    type="radio"
                    name={currentQuestion.id}
                    value={option.value}
                    checked={answers[currentQuestion.id] === option.value}
                    onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                    className="option-checkbox"
                  />
                  {option.label}
                </label>
              ))}
            </div>
            
            {/* Show pass purchase info if they don't have a pass */}
            {answers[currentQuestion.id] === 'no' && currentQuestion.noPassInfo && (
              <div style={{ 
                marginTop: '20px',
                padding: '20px',
                background: 'linear-gradient(135deg, #e6f3ff 0%, #b3d9ff 100%)',
                borderRadius: '12px',
                border: '2px solid #4a90e2',
                animation: 'fadeIn 0.3s ease'
              }}>
                <h4 style={{ 
                  margin: '0 0 12px 0',
                  color: '#2c5282',
                  fontSize: '1.1rem',
                  fontWeight: '600'
                }}>
                  {currentQuestion.noPassInfo.title}
                </h4>
                <p style={{ 
                  margin: '0 0 16px 0',
                  color: '#2d3748',
                  fontSize: '0.95rem'
                }}>
                  {currentQuestion.noPassInfo.description}
                </p>
                <ul style={{ 
                  margin: '0 0 16px 0',
                  paddingLeft: '20px',
                  color: '#2d3748'
                }}>
                  {currentQuestion.noPassInfo.info.map((item, index) => (
                    <li key={index} style={{ marginBottom: '4px', fontSize: '0.9rem' }}>
                      {item}
                    </li>
                  ))}
                </ul>
                <a
                  href={currentQuestion.noPassInfo.purchaseLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-block',
                    background: 'linear-gradient(135deg, #4a90e2 0%, #357abd 100%)',
                    color: 'white',
                    padding: '12px 24px',
                    borderRadius: '12px',
                    textDecoration: 'none',
                    fontWeight: '600',
                    fontSize: '0.95rem',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 4px rgba(74, 144, 226, 0.3)'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 4px 8px rgba(74, 144, 226, 0.4)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 2px 4px rgba(74, 144, 226, 0.3)';
                  }}
                >
                  {currentQuestion.noPassInfo.linkText} →
                </a>
              </div>
            )}
          </div>
        );

      case 'multiSelect':
        return (
          <div>
            {currentQuestion.showSelectAll && (
              <div style={{ 
                display: 'flex', 
                gap: '10px', 
                marginBottom: '15px',
                justifyContent: 'center'
              }}>
                <button
                  type="button"
                  onClick={() => handleSelectAll(currentQuestion.id, currentQuestion.options)}
                  style={{
                    background: '#e6fffa',
                    border: '2px solid #81e6d9',
                    color: '#319795',
                    padding: '8px 16px',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '600'
                  }}
                >
                  Select All
                </button>
                <button
                  type="button"
                  onClick={() => handleDeselectAll(currentQuestion.id)}
                  style={{
                    background: '#fed7d7',
                    border: '2px solid #feb2b2',
                    color: '#c53030',
                    padding: '8px 16px',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '600'
                  }}
                >
                  Clear All
                </button>
              </div>
            )}
            <div className="option-group">
              {currentQuestion.options.map((option) => (
                <label
                  key={option.value}
                  className={`option-item ${(answers[currentQuestion.id] || []).includes(option.value) ? 'selected' : ''}`}
                >
                  <input
                    type="checkbox"
                    value={option.value}
                    checked={(answers[currentQuestion.id] || []).includes(option.value)}
                    onChange={(e) => {
                      const currentAnswers = answers[currentQuestion.id] || [];
                      if (e.target.checked) {
                        handleAnswerChange(currentQuestion.id, [...currentAnswers, option.value]);
                      } else {
                        handleAnswerChange(currentQuestion.id, currentAnswers.filter(a => a !== option.value));
                      }
                    }}
                    className="option-checkbox"
                  />
                  {option.label}
                </label>
              ))}
            </div>
            
            {/* Show text input if "other" is selected for accommodation */}
            {currentQuestion.id === 'accommodation' && (answers[currentQuestion.id] || []).includes('other') && (
              <div style={{ 
                marginTop: '20px',
                padding: '15px',
                background: '#f0f9ff',
                borderRadius: '12px',
                border: '1px solid #bae6fd',
                animation: 'fadeIn 0.3s ease'
              }}>
                <label style={{ 
                  display: 'block',
                  marginBottom: '8px',
                  color: '#0ea5e9',
                  fontWeight: '600',
                  fontSize: '0.95rem'
                }}>
                  Please specify:
                </label>
                <input
                  type="text"
                  value={answers['accommodation_other'] || ''}
                  onChange={(e) => handleAnswerChange('accommodation_other', e.target.value)}
                  placeholder="e.g., Pet-friendly, elevator access, etc."
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #bae6fd',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none'
                  }}
                />
              </div>
            )}
          </div>
        );

      case 'slider':
        const currentValue = answers[currentQuestion.id] || currentQuestion.default;
        const isAtMax = currentValue >= currentQuestion.max;
        
        return (
          <div className="slider-container">
            {currentQuestion.showLabels && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                marginBottom: '10px',
                fontSize: '0.9rem',
                color: '#718096'
              }}>
                <span>{currentQuestion.minLabel || `$${currentQuestion.min}`}</span>
                <span>{currentQuestion.maxLabel || `$${currentQuestion.max}`}</span>
              </div>
            )}
            <input
              type="range"
              min={currentQuestion.min}
              max={currentQuestion.max}
              step={currentQuestion.step}
              value={currentValue}
              onChange={(e) => handleAnswerChange(currentQuestion.id, parseInt(e.target.value))}
              className="slider"
            />
            <div className="slider-value">
              ${currentValue}
            </div>
            
            {/* Max value checkbox */}
            {currentQuestion.maxValueCheckbox?.show && isAtMax && (
              <div style={{ 
                marginTop: '20px',
                padding: '15px',
                background: 'linear-gradient(135deg, #fef5e7 0%, #f6e05e 100%)',
                borderRadius: '12px',
                border: '2px solid #f6e05e',
                animation: 'fadeIn 0.3s ease'
              }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  cursor: 'pointer',
                  margin: 0
                }}>
                  <input
                    type="checkbox"
                    checked={answers[currentQuestion.id + '_maxCheckbox'] || false}
                    onChange={(e) => handleAnswerChange(currentQuestion.id + '_maxCheckbox', e.target.checked)}
                    style={{ 
                      marginRight: '12px',
                      width: '18px',
                      height: '18px',
                      accentColor: '#d69e2e'
                    }}
                  />
                  <div>
                    <div style={{ 
                      fontWeight: '700',
                      color: '#d69e2e',
                      fontSize: '1.1rem',
                      marginBottom: '4px'
                    }}>
                      {currentQuestion.maxValueCheckbox.label}
                    </div>
                    <div style={{ 
                      color: '#b7791f',
                      fontSize: '0.9rem',
                      fontStyle: 'italic'
                    }}>
                      {currentQuestion.maxValueCheckbox.description}
                    </div>
                  </div>
                </label>
              </div>
            )}
          </div>
        );

      case 'checkboxGroup':
        return (
          <div className="option-group">
            {currentQuestion.options.map((option) => {
              const isChecked = answers[currentQuestion.id]?.[option.id];
              const textValue = typeof isChecked === 'string' ? isChecked : '';
              
              return (
                <div key={option.id} style={{ marginBottom: '15px' }}>
                  <label className="option-item" style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    padding: '16px 20px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    background: isChecked ? '#f0f9ff' : '#f7fafc',
                    borderColor: isChecked ? '#0ea5e9' : '#e2e8f0'
                  }}>
                    <input
                      type="checkbox"
                      checked={!!isChecked}
                      onChange={(e) => handleCheckboxGroupChange(
                        currentQuestion.id, 
                        option.id, 
                        e.target.checked,
                        textValue
                      )}
                      className="option-checkbox"
                      style={{ marginRight: '12px' }}
                    />
                    <span style={{ 
                      fontWeight: isChecked ? '600' : '400',
                      color: isChecked ? '#0ea5e9' : '#4a5568'
                    }}>
                      {option.label}
                    </span>
                  </label>
                  
                  {option.hasTextInput && isChecked && (
                    <div style={{ 
                      marginTop: '10px', 
                      marginLeft: '32px',
                      animation: 'fadeIn 0.3s ease'
                    }}>
                      <textarea
                        className="form-input"
                        value={textValue}
                        onChange={(e) => handleCheckboxGroupChange(
                          currentQuestion.id, 
                          option.id, 
                          true,
                          e.target.value
                        )}
                        placeholder={option.textInputPlaceholder}
                        rows="3"
                        style={{ 
                          resize: 'vertical', 
                          minHeight: '80px',
                          fontSize: '0.9rem'
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );

      case 'text':
        return (
          <textarea
            className="form-input"
            value={answers[currentQuestion.id] || ''}
            onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
            placeholder={currentQuestion.placeholder}
            rows="4"
            style={{ resize: 'vertical', minHeight: '100px' }}
          />
        );

      default:
        return null;
    }
  };

  const isDevMode = localStorage.getItem('devMode') === 'true';
  if (!currentUser && !isDevMode) {
    return <div className="loading">Loading...</div>;
  }

  // Check if user is an admin
  const isAdmin = currentUser && ADMIN_PHONE_NUMBERS.includes(currentUser.phoneNumber);

  return (
    <div className="container">
      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '15px' }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <h1 className="card-title">🏔️ {questionsConfig.tripInfo.title}</h1>
              <p className="card-subtitle">{questionsConfig.tripInfo.description}</p>
            </div>
            {isAdmin && (
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <button 
                  onClick={() => navigate('/admin')}
                  className="btn btn-secondary"
                  style={{ 
                    width: 'auto', 
                    padding: '8px 16px', 
                    fontSize: '0.9rem',
                    borderRadius: '8px'
                  }}
                >
                  ← Back to Admin
                </button>
              </div>
            )}
          </div>
          {isAdmin && (
            <div style={{ 
              background: '#fef5e7', 
              padding: '12px', 
              borderRadius: '8px', 
              marginTop: '15px',
              border: '1px solid #f6e05e'
            }}>
              <p style={{ margin: 0, color: '#d69e2e', fontSize: '0.9rem', fontWeight: '600' }}>
                👤 Admin View: You are viewing the survey as an administrator
              </p>
            </div>
          )}
          {!isAdmin && (
            <div style={{ 
              background: '#fff3cd', 
              padding: '16px', 
              borderRadius: '8px', 
              marginTop: '15px',
              border: '2px solid #ffc107'
            }}>
              <p style={{ margin: '0 0 8px 0', color: '#856404', fontSize: '1rem', fontWeight: '700' }}>
                ⚠️ Sign-up for the trip has closed
              </p>
              <p style={{ margin: 0, color: '#856404', fontSize: '0.9rem' }}>
                If you fill out this form, you will be placed on a waitlist. We'll contact you if spots become available.
              </p>
            </div>
          )}
        </div>

        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${progress}%` }}
          ></div>
        </div>

        {error && <div className="error">{error}</div>}

        <div className="question-card">
          <h2 className="question-title">{currentQuestion.title}</h2>
          {currentQuestion.description && (
            <p className="question-description">{currentQuestion.description}</p>
          )}
          
          {renderQuestion()}
        </div>

        <div className="navigation">
          {currentQuestionIndex > 0 && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handlePrevious}
              disabled={loading}
            >
              Previous
            </button>
          )}
          
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleNext}
            disabled={loading || !isCurrentQuestionValid()}
          >
            {loading ? 'Submitting...' : 
             currentQuestionIndex === questions.length - 1 ? 'Submit' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Survey;
